import { API } from './axios';
import { URLS } from './Urls';

export const quoteService = {
  // Récupérer la liste des devis
  getQuotes: async (params = {}) => {
    try {
      const response = await API.get(URLS.QUOTE.list, { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des devis:', error);
      throw error;
    }
  },

  // Récupérer tous les devis (gestion de la pagination avec limit/offset)
  getAllQuotes: async () => {
    try {
      // D'abord, récupérer la première page pour connaître le total
      console.log('Récupération de la première page pour connaître le total...');
      const firstResponse = await API.get(URLS.QUOTE.list, { 
        params: { 
          limit: 25, 
          offset: 0
        }
      });
      
      const firstData = firstResponse.data;
      console.log('Première page - Total count:', firstData.count);
      console.log('Première page - Résultats:', firstData.results ? firstData.results.length : 0);
      
      let allQuotes = [];
      const seenIds = new Set();
      
      // Ajouter les résultats de la première page
      if (firstData.results && firstData.results.length > 0) {
        allQuotes = allQuotes.concat(firstData.results);
        firstData.results.forEach(quote => seenIds.add(quote.id));
        console.log('Première page - Devis ajoutés:', firstData.results.length);
      }
      
      // Si le total est supérieur à ce qu'on a récupéré, continuer la pagination
      const totalCount = firstData.count || 0;
      const currentCount = allQuotes.length;
      
      if (totalCount > currentCount) {
        console.log(`Total: ${totalCount}, Récupérés: ${currentCount}, Continuation de la pagination...`);
        
        let offset = 25; // Commencer à l'offset 25 (deuxième page)
        let hasNextPage = firstData.next !== null && firstData.next !== undefined && firstData.next !== '';
        
        while (hasNextPage && allQuotes.length < totalCount) {
          console.log(`Récupération avec offset ${offset}...`);
          const response = await API.get(URLS.QUOTE.list, { 
            params: { 
              limit: 25, 
              offset: offset
            }
          });
          
          const data = response.data;
          console.log(`Offset ${offset} - Résultats reçus:`, data.results ? data.results.length : 0);
          console.log(`Offset ${offset} - Total count:`, data.count);
          console.log(`Offset ${offset} - hasNextPage:`, data.next);
          
          if (data.results && data.results.length > 0) {
            // Filtrer les doublons basés sur l'ID
            const newQuotes = data.results.filter(quote => {
              if (seenIds.has(quote.id)) {
                console.warn(`Doublon détecté - ID: ${quote.id}`);
                return false;
              }
              seenIds.add(quote.id);
              return true;
            });
            
            allQuotes = allQuotes.concat(newQuotes);
            console.log(`Offset ${offset} - Nouveaux devis ajoutés:`, newQuotes.length);
          }
          
          // Vérifier s'il y a une page suivante
          hasNextPage = data.next !== null && data.next !== undefined && data.next !== '';
          
          // Si on n'a pas de résultats sur cette page, arrêter
          if (!data.results || data.results.length === 0) {
            console.log(`Offset ${offset} - Page vide, arrêt de la récupération`);
            break;
          }
          
          offset += 25; // Incrémenter l'offset de 25
          
          // Limite de sécurité
          if (offset > 1000) {
            console.warn('Limite d\'offset atteinte (1000), arrêt de la récupération');
            break;
          }
        }
      } else {
        console.log('Tous les devis récupérés en une seule page');
      }

      console.log(`Total des devis récupérés (sans doublons): ${allQuotes.length}`);
      console.log(`Total attendu: ${totalCount}`);
      console.log(`IDs uniques vus: ${seenIds.size}`);
      
      return {
        results: allQuotes,
        count: allQuotes.length,
        next: null,
        previous: null
      };
    } catch (error) {
      console.error('Erreur lors de la récupération de tous les devis:', error);
      throw error;
    }
  },

  // Récupérer les détails d'un devis
  getQuoteDetails: async (quoteId) => {
    try {
      const response = await API.get(URLS.QUOTE.details(quoteId));
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération du devis ${quoteId}:`, error);
      throw error;
    }
  },

  // Transformer les données du devis pour l'affichage
  transformQuoteForDisplay: (quote) => {
    return {
      id: quote.id,
      name: quote.name,
      email: quote.email,
      phone: quote.phone_number,
      siteAddress: quote.site_address,
      postalCode: quote.postal_code,
      city: quote.city,
      needInstaller: quote.need_installer,
      beReminded: quote.be_reminded,
      deliveryInstructions: quote.delivery_instructions,
      comments: quote.comments,
      userInput: quote.user_input,
      itemsCount: quote.items ? quote.items.length : 0,
      items: quote.items || [],
      // Calculer le statut basé sur les données disponibles
      status: quote.be_reminded ? 'En attente' : 'Traité',
      statusType: quote.be_reminded ? 'warning' : 'success',
      // Date de création (date de soumission du devis)
      date: quote.created_at ? new Date(quote.created_at).toLocaleDateString('fr-FR') : 'N/A',
      // Montant (à calculer si nécessaire)
      montant: 'À calculer'
    };
  },

  // Transformer la liste des devis pour l'affichage
  transformQuotesList: (quotesData) => {
    if (!quotesData || !quotesData.results) {
      return [];
    }
    
    return quotesData.results.map((quote, index) => ({
      id: quote.id,
      roll: (index + 1).toString().padStart(2, '0'),
      nom: quote.name,
      mail: quote.email,
      projet: quote.user_input || 'Non spécifié',
      adress: `${quote.site_address}, ${quote.postal_code} ${quote.city}`,
      status: quote.be_reminded ? 'En attente' : 'Traité',
      statusType: quote.be_reminded ? 'warning' : 'success',
      date: quote.created_at ? new Date(quote.created_at).toLocaleDateString('fr-FR') : 'N/A',
      montant: 'À calculer',
      phone: quote.phone_number,
      needInstaller: quote.need_installer,
      deliveryInstructions: quote.delivery_instructions,
      comments: quote.comments,
      itemsCount: quote.items ? quote.items.length : 0
    }));
  }
};
